import { createRouter, createWebHistory } from 'vue-router'
import Auth from '@/views/Auth.vue'
import Login from '@/components/Auth/Login.vue'
import PwResetRequest from '@product/components/Auth/PwResetRequest.vue'
import PwReset from '@product/components/Auth/PwReset.vue'

import UserIndex from '@product/views/UserIndex.vue'

/////////////////// AUTHENTICATED /////////////////////////
import Home from '@product/views/Home.vue'
import Dashboard from '@product/views/Dashboard.vue'

// QUERY VIEWS
import Query from '@/views/Authenticated/Query/Query.vue'

// SIGNALS
import Signals from '@product/views/Signals/Signals.vue'

// NOTIFICATIONS
import Notifications from '@/views/Authenticated/Notifications.vue'

// GUIDES
import Guides from '@/views/Authenticated/Guides.vue'

// EVENTS (new layout with nested routes)
import EventSearchLayout from '@product/views/EventSearch/EventSearchLayout.vue'
import EventSearchLandingView from '@/views/Authenticated/EventSearch/LandingView.vue'
import EventSearchDefaultView from '@/views/Authenticated/EventSearch/DefaultView.vue'
import EventSearchQueryHistory from '@/views/Authenticated/EventSearch/QueryHistoryView.vue'

import QueryRunner from '@/views/Authenticated/Query/Runner.vue'

// ACCOUNT
import AccountHome from '@/views/Authenticated/Account/Home.vue'
import AccountInfo from '@/views/Authenticated/Account/Information.vue'
import AccountApiKeys from '@/views/Authenticated/Account/ApiKeys.vue'

// DOCUMENTATION VIEWS
import DocsIndex from '@product/views/Docs/Index.vue'
import ApiReferenceIndex from '@product/views/Docs/Api/Index.vue'

import { useUserStore } from '@/stores/user'

// First value of a raw (possibly comma-separated, possibly repeated) ?input=
// query param — used by the legacy /enrich single-result redirects to seed
// the SelectorResults zoom drill-in.
function firstSelector(input) {
  const raw = Array.isArray(input) ? input[0] : input
  if (!raw) return undefined
  return String(raw).split(',')[0].trim() || undefined
}

// Decode search input from URL parameter
// Handles arrays (comma-separated) and single values
function decodeSearchInput(input) {
  if (!input) return []

  try {
    // If it looks like a comma-separated list, split it
    if (typeof input === 'string' && input.includes(',')) {
      return input.split(',').map(item => decodeURIComponent(item.trim()))
    }
    // Single value
    return [decodeURIComponent(input)]
  } catch (e) {
    console.error('Failed to decode search input:', e)
    return []
  }
}

const routes = [
  // Auth routes (unauthenticated users)
  {
    path: '/',
    component: Auth,
    children: [
      {
        path: '',
        redirect: 'login'
      },
      {
        path: 'login',
        name: 'Login',
        component: Login,
      },
      {
        path: 'password-reset/:token',
        name: 'PasswordReset',
        component: PwReset,
        props: true,
      },
      {
        path: 'pw-reset-request',
        name: 'PwResetRequest',
        component: PwResetRequest,
      },
    ]
  },
  // Authenticated routes
  {
    path: '',
    name: 'UserIndex',
    component: UserIndex,
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: '',
        redirect: '/home'
      },
      {
        path: 'home',
        name: 'Home',
        component: Home,
        meta: {
          requiresAuth: true
        }
      },
      {
        // Legacy /enrich family — fully replaced by Selector Search. The old
        // paths and names are kept as redirects so bookmarked URLs and
        // push-by-name callers land on the new pages.
        path: 'enrich',
        name: 'Enrich',
        redirect: { name: 'SelectorSearch' },
      },
      {
        path: 'search/selector',
        name: 'SelectorSearch',
        component: () => import('@product/views/SelectorSearch/SelectorSearchView.vue'),
      },
      {
        // Results page: state lives in the route query — input/types/log from
        // the search page, tab/zoom owned by the results view (drill-in is a
        // query change: shareable, browser-back works).
        path: 'search/selector/results',
        name: 'SelectorResults',
        component: () => import('@product/views/SelectorSearch/SelectorResultsView.vue'),
        props: route => ({
          input: route.query.input || '',
          types: (route.query.types || '').split(',').filter(Boolean),
          log: route.query.log !== 'false'
        })
      },
      {
        path: 'search',
        name: 'Search',
        component: Query,
      },
      {
        // Legacy single-result view → results page zoomed on the first value.
        // The raw ?input= passes through as-is (SelectorResults carves it).
        path: 'enrich/ip',
        name: 'EnrichIpResults',
        redirect: to => ({
          name: 'SelectorResults',
          query: {
            input: to.query.input,
            tab: 'ip',
            zoom: firstSelector(to.query.input),
            log: to.query.log
          }
        })
      },
      {
        path: 'enrich/ip/table',
        name: 'EnrichIpTable',
        redirect: to => ({
          name: 'SelectorResults',
          query: { input: to.query.input, tab: 'ip', log: to.query.log }
        })
      },
      {
        // The old ?type= (ja4/ja4h/…) is dropped: the carver detects the
        // JA4+ subtype from the fingerprint itself.
        path: 'enrich/ja4',
        name: 'EnrichJa4Results',
        redirect: to => ({
          name: 'SelectorResults',
          query: {
            input: to.query.input,
            tab: 'ja4',
            zoom: firstSelector(to.query.input),
            log: to.query.log
          }
        })
      },
      {
        path: 'enrich/ja4/table',
        name: 'EnrichJa4Table',
        redirect: to => ({
          name: 'SelectorResults',
          query: { input: to.query.input, tab: 'ja4', log: to.query.log }
        })
      },
      {
        path: 'enrich/domain',
        name: 'EnrichDomainResults',
        redirect: to => ({
          name: 'SelectorResults',
          query: {
            input: to.query.input,
            tab: 'domain',
            zoom: firstSelector(to.query.input),
            log: to.query.log
          }
        })
      },
      {
        path: 'enrich/domain/table',
        name: 'EnrichDomainTable',
        redirect: to => ({
          name: 'SelectorResults',
          query: { input: to.query.input, tab: 'domain', log: to.query.log }
        })
      },
      {
        path: 'search/ip',
        name: 'SearchResults',
        component: () => import('@/views/Authenticated/Search/SearchResults.vue'),
        props: route => ({
          input: decodeSearchInput(route.query.input),
          type: route.query.type,
          log: route.query.log !== 'false' // default to true unless explicitly false
        })
      },
      {
        path: 'admin/providers',
        name: 'AdminProviders',
        component: () => import('@/views/Authenticated/AdminProviders.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'search/event',
        component: EventSearchLayout,
        children: [
          {
            path: '',
            name: 'EventSearch',
            component: EventSearchLandingView
          },
          {
            path: 'table',
            name: 'EventSearchTable',
            component: EventSearchDefaultView
          },
          {
            path: 'history',
            name: 'EventSearchHistory',
            component: EventSearchQueryHistory
          },
          {
            path: 'aggregations',
            name: 'EventSearchAggregations',
            component: () => import('@product/views/EventSearch/AggregationsView.vue')
          },
          {
            path: 'map',
            name: 'EventSearchMap',
            component: () => import('@/views/Authenticated/EventSearch/MapView.vue')
          }
        ]
      },
      {
        // Backwards compatibility - redirect old routes
        path: 'event-search',
        redirect: to => ({
          path: '/search/event',
          query: to.query
        })
      },
      {
        path: 'event/search',
        redirect: to => ({
          path: '/search/event',
          query: to.query
        })
      },
      {
        path: 'saved-searches',
        name: 'SavedSearches',
        component: () => import('@/views/Authenticated/SavedSearches.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'admin/accounts',
        name: 'AdminAccounts',
        component: () => import('@/views/Authenticated/AdminAccounts.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/organizations',
        name: 'AdminOrganizations',
        component: () => import('@/views/Authenticated/AdminOrganizations.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/notifications',
        name: 'AdminNotifications',
        component: () => import('@/views/Authenticated/AdminNotifications.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/news',
        name: 'AdminNews',
        component: () => import('@/views/Authenticated/AdminNews.vue'),
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'admin/news/edit/:id?',
        name: 'AdminNewsEditor',
        component: () => import('@/views/Authenticated/AdminNewsEditor.vue'),
        props: true,
        meta: {
          requiresAuth: true,
          requiresAdmin: true
        }
      },
      {
        path: 'news',
        name: 'News',
        component: () => import('@product/views/NewsView.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'signals',
        name: 'Signals',
        component: Signals,
        props: true
      },
      {
        // Grouping record only, no component of its own — the children render
        // into UserIndex's router-view. Nesting is what keeps the sidebar's
        // /notebook link lit inside a notebook: router-link matches on route
        // records, so as flat siblings /notebook/:id left the link dark.
        path: 'notebook',
        children: [
          {
            path: '',
            name: 'Notebooks',
            component: () => import('@product/views/NotebookIndex.vue'),
            meta: {
              requiresAuth: true,
              requiresAdmin: true
            }
          },
          {
            path: ':id',
            name: 'ThreatHuntNotebook',
            component: () => import('@product/views/ThreatHuntNotebook.vue'),
            props: true,
            meta: {
              requiresAuth: true,
              requiresAdmin: true
            }
          }
        ]
      },
      {
        path: 'notifications',
        name: 'Notifications',
        component: Notifications,
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'guides',
        name: 'Guides',
        component: Guides,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "runner",
        component: QueryRunner,
        name: "QueryRunner",
        props: true
      },
      {
        path: 'account',
        name: 'Account',
        component: AccountHome,
        children: [
          {
            path: '',
            name: 'AccountInfo',
            component: AccountInfo,
            children: []
          },
          {
            path: 'apikeys',
            name: 'AccountApiKeys',
            component: AccountApiKeys,
            children: []
          },
        ]
      },
      {
        path: 'selector/integrations',
        name: 'SelectorIntegrations',
        component: () => import('@/views/Authenticated/Sources/EnrichmentsView.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'selector/integration/:slug',
        name: 'SelectorIntegration',
        component: () => import('@/views/Authenticated/Sources/EnrichmentDashboard.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'flow/integrations',
        name: 'FlowIntegrations',
        component: () => import('@/views/Authenticated/Sources/SourcesView.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'flow/source/:id',
        name: 'FlowSource',
        component: () => import('@/views/Authenticated/Sources/SourceDashboard.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'flow/group/:id',
        name: 'FlowGroup',
        component: () => import('@/views/Authenticated/Sources/GroupDashboard.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        path: 'flow/reference',
        name: 'FlowReference',
        component: () => import('@product/views/QueryReference.vue'),
        meta: {
          requiresAuth: true
        }
      },
      {
        // Backwards compatibility - old /sources tab family
        path: 'sources',
        redirect: to => ({
          path: '/flow/integrations',
          query: to.query
        })
      },
      {
        path: 'sources/list',
        redirect: to => ({
          path: '/flow/integrations',
          query: to.query
        })
      },
      {
        path: 'sources/enrichments',
        redirect: to => ({
          path: '/selector/integrations',
          query: to.query
        })
      },
      {
        path: 'docs',
        name: 'DocsIndex',
        component: DocsIndex,
        children: [
          {
            path: 'api',
            name: 'UserApiReference',
            component: ApiReferenceIndex,
          },
        ]
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: { name: 'Home' }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  path: process.env.VUE_APP_PATH,
  routes
})

router.beforeEach( (to, from, next) => {
  let userStore = useUserStore()

  // Admin routes check (most restrictive)
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    if (!userStore.loggedIn) {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
    if (userStore.accountType !== 1337) {
      return next({ name: 'Home' })
    }
    return next()
  }

  // Authenticated routes check
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (userStore.loggedIn) {
      return next()
    } else {
      return next({ name: 'Login', query: { redirect: to.fullPath } })
    }
  }

  // Login/auth pages - redirect to dashboard if already logged in
  if (to.name === 'Login' || to.name === 'PwResetRequest') {
    if (userStore.loggedIn) {
      return next({ name: 'Home' })
    }
  }

  // Default - proceed
  return next()
});

export default router
